import { useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { qualityAuditApi } from "../../api/qualityAuditApi";
import { trackToolAction } from "../../utils/analytics";

const VideoAuditorTab = () => {
  const [videoUrlOrId, setVideoUrlOrId] = useState("");
  const [includeTranscript, setIncludeTranscript] = useState(false);
  const [loading, setLoading] = useState(false);
  const [auditText, setAuditText] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const toast = useRef<Toast>(null);

  const runAudit = async (asPdf: boolean) => {
    if (!videoUrlOrId.trim()) {
      toast.current?.show({ severity: "warn", summary: "Enter a video URL or ID" });
      return;
    }
    trackToolAction("Classifiers", "run_audit");
    setLoading(true);
    setAuditText(null);
    setRunId(null);
    try {
      if (asPdf) {
        const blob = await qualityAuditApi.videoAudit({
          videoUrlOrId: videoUrlOrId.trim(),
          includeVisualTranscript: includeTranscript,
          pdf: true,
        }) as Blob;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "quality-audit.pdf");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.current?.show({ severity: "success", summary: "PDF downloaded" });
      } else {
        const result = await qualityAuditApi.videoAudit({
          videoUrlOrId: videoUrlOrId.trim(),
          includeVisualTranscript: includeTranscript,
        }) as { runId: string; auditText: string };
        setRunId(result.runId);
        setAuditText(result.auditText);
        toast.current?.show({ severity: "success", summary: "Audit complete" });
      }
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "response" in e
        ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
        : (e as Error)?.message;
      toast.current?.show({ severity: "error", summary: "Error", detail: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    if (!runId) return;
    trackToolAction("Classifiers", "download");
    qualityAuditApi.downloadExport(runId).catch((e) => {
      toast.current?.show({ severity: "error", summary: "Download failed", detail: String(e) });
    });
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <h3 className="mt-0">Video Auditor</h3>
      <p className="text-color-secondary mb-4">
        Enter a YouTube video URL or ID to run a single-video quality audit.
      </p>
      <div className="flex flex-column gap-3 mb-4">
        <label htmlFor="video-input">Video URL or ID</label>
        <InputText
          id="video-input"
          value={videoUrlOrId}
          onChange={(e) => setVideoUrlOrId(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=... or video ID"
          className="w-full"
        />
        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="transcript"
            checked={includeTranscript}
            onChange={(e) => setIncludeTranscript(e.checked ?? false)}
          />
          <label htmlFor="transcript">Include Visual Transcript</label>
        </div>
        <div className="flex gap-2">
          <Button
            label="Run Audit"
            icon="pi pi-check"
            loading={loading}
            disabled={loading}
            onClick={() => runAudit(false)}
          />
          <Button
            label="Download PDF"
            icon="pi pi-file-pdf"
            loading={loading}
            disabled={loading}
            onClick={() => runAudit(true)}
            severity="secondary"
          />
          {runId && (
            <Button
              label="Download CSV"
              icon="pi pi-download"
              onClick={handleDownloadCsv}
              severity="help"
            />
          )}
        </div>
      </div>
      {auditText && (
        <div className="mt-4 p-4 surface-100 border-round">
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0 }}>
            {auditText}
          </pre>
        </div>
      )}
    </div>
  );
};

export default VideoAuditorTab;
